import { motion, type Variants } from 'framer-motion';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  memo,
  type PropsWithChildren,
  type ReactNode,
  type ComponentType as ReactComponentType,
  type HTMLAttributes as ReactHTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type Ref,
} from 'react';
import { Box } from '~/commonComponents/Box';
import { Center } from '~/commonComponents/Center';
import { Column } from '~/commonComponents/Column';
import { Row } from '~/commonComponents/Row';
import ArrowRightIcon from '~/images/arrow_right.svg';
import { Colors } from '~/themes/colors';
import { Title } from './Title';
import { Value } from './Value';

const CollapsableVariants = {
  open: {
    maxHeight: 2000,
    opacity: 1,
    marginTop: 30,
    paddingBottom: 40,
    overflow: 'visible',
  },
  closed: {
    maxHeight: 0,
    opacity: 0,
    overflow: 'hidden',
  },
};

export const ArrowVariants = {
  open: {
    rotate: -90,
  },
  closed: {
    rotate: 90,
  },
};

export interface IFieldProps {
  ComponentProps?: Partial<{
    Button: ButtonHTMLAttributes<HTMLButtonElement> & {
      [key: `data-${string}`]: string | undefined;
    };
    Container: ReactHTMLAttributes<HTMLDivElement> & {
      [key: `data-${string}`]: string | undefined;
    };
    Icon: ReactHTMLAttributes<HTMLImageElement>;
  }>;
  IconSrc: string;
  TitleComponent?: ReactComponentType;
  ValueComponent?: ReactComponentType;
  SuffixComponent?: (props: { isOpen: boolean; variants: Variants }) => ReactNode;
  hideArrow?: boolean;
  isDisabled?: boolean;
  isOpen?: boolean;
  isRequired?: boolean;
  title?: string;
  value?: string;
  onToggle?: () => void;
}

const Component = (
  {
    ComponentProps = { Button: {}, Container: {}, Icon: {} },
    IconSrc,
    TitleComponent,
    ValueComponent,
    SuffixComponent,
    children,
    hideArrow = false,
    isDisabled = false,
    isOpen = false,
    isRequired = false,
    title = '',
    value = '',
    onToggle,
  }: PropsWithChildren<IFieldProps>,
  ref: Ref<HTMLDivElement>,
) => {
  const handleTriggerClick = (e: ReactMouseEvent) => {
    if (isDisabled) return;

    e.stopPropagation();
    onToggle?.();
  };

  return (
    <Column
      ref={ref}
      borderColor={isOpen ? Colors.secondary_500 : Colors.neutral_100}
      borderRadius={8}
      borderStyle="solid"
      borderWidth={1.5}
      {...ComponentProps.Container}
    >
      <button
        type="button"
        {...ComponentProps.Button}
        style={{
          all: 'unset',
          width: '100%',
          boxSizing: 'border-box',
          alignItems: 'center',
          cursor: isDisabled ? 'default' : 'pointer',
          padding: '20px 32px',
        }}
        onClick={handleTriggerClick}
      >
        <Row
          alignItems="flex-start"
          cursor={isDisabled ? 'default' : 'pointer'}
          justifyContent="space-between"
        >
          <Row>
            <Center width={24} height={24} marginRight={12}>
              <img
                alt="prefix-icon"
                src={IconSrc}
                {...ComponentProps.Icon}
                style={{
                  height: '100%',
                  width: '100%',
                  ...ComponentProps.Icon?.style,
                }}
              />
            </Center>
            {TitleComponent ? <TitleComponent /> : <Title isRequired={isRequired} text={title} />}
          </Row>

          <Box flex={1} />

          {ValueComponent ? <ValueComponent /> : <Value text={value} />}

          <Center paddingLeft={16}>
            {!hideArrow && !isDisabled && (
              SuffixComponent ? (
                SuffixComponent({ isOpen, variants: ArrowVariants })
              ) : (
                <motion.img
                  initial="closed"
                  animate={isOpen ? 'open' : 'closed'}
                  transition={{ duration: 0.4 }}
                  variants={ArrowVariants}
                  alt="arrow-right-icon"
                  src={ArrowRightIcon}
                />
              )
            )}
          </Center>
        </Row>
      </button>

      <motion.div
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        transition={{ duration: 0.5 }}
        style={{
          height: 'auto',
          overflow: 'hidden',
          backgroundColor: Colors.white,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        variants={CollapsableVariants}
      >
        {children}
      </motion.div>
    </Column>
  );
};

export const Field = memo(forwardRef(Component));
